/**
 * Live activity stream for a Document Intelligence request.
 *
 * FROZEN CONTRACT — published by the backend workstream and consumed here verbatim.
 * Nothing in this file redefines a shape, invents an endpoint, or assumes a behaviour
 * that is not in the contract below.
 *
 *   GET /api/docint/assistant/threads/:threadId/stream          (text/event-stream)
 *
 *     event: stage      data: {"key":"retrieve","label":"Reading documents",
 *                              "status":"running"|"succeeded"|"failed"}
 *     event: activity   data: {"text":"Found 14 candidate clauses on confidentiality","detail":{}}
 *     event: reasoning  data: {"text":"..."}      // only arrives when the toggle is on
 *     event: done       data: {"status":"succeeded"|"failed","message_id":"<uuid>"}
 *     event: error      data: {"message":"..."}
 *
 * WHY THIS IS NOT `EventSource`.
 * `EventSource` cannot set request headers. Every codebase that reaches for it therefore
 * ends up moving the bearer token into the query string, where it is written to access
 * logs, proxy logs, referrers and browser history, and where it outlives the request.
 * This reads the same `text/event-stream` body over `fetch`, which CAN carry
 * `Authorization`. The token never appears in a URL. `activityStreamAuth.test.ts` pins
 * that property directly rather than trusting this comment.
 */

// ── Contract types ───────────────────────────────────────────────────────────

export type StageStatus = "running" | "succeeded" | "failed";

export interface StagePayload {
  key: string;
  label: string;
  status: StageStatus;
}

export interface ActivityPayload {
  text: string;
  detail?: Record<string, unknown> | null;
}

export interface ReasoningPayload {
  text: string;
}

export interface DonePayload {
  status: "succeeded" | "failed";
  message_id?: string;
}

export interface ErrorPayload {
  message: string;
}

export type ActivityStreamEvent =
  | { type: "stage"; data: StagePayload }
  | { type: "activity"; data: ActivityPayload }
  | { type: "reasoning"; data: ReasoningPayload }
  | { type: "done"; data: DonePayload }
  | { type: "error"; data: ErrorPayload };

/** The event names this client understands. Anything else on the wire is ignored. */
const KNOWN_EVENTS = new Set(["stage", "activity", "reasoning", "done", "error"]);

// ── Reduced state ────────────────────────────────────────────────────────────

export interface ActivityStage {
  key: string;
  label: string;
  status: StageStatus;
}

export interface ActivityLine {
  id: string;
  text: string;
  detail?: Record<string, unknown> | null;
}

export interface ActivityTerminal {
  status: "succeeded" | "failed";
  messageId?: string;
  message?: string;
}

export interface ActivityStreamState {
  /** First-appearance order. A stage never moves and never reappears twice. */
  stages: ActivityStage[];
  /** The narration, oldest first. */
  activity: ActivityLine[];
  /** Model working-out. Only ever populated when the user asked for it. */
  reasoning: ActivityLine[];
  /** Set exactly once. Everything after it is ignored. */
  terminal: ActivityTerminal | null;
  /** Monotonic counter used only to key list items. */
  seq: number;
}

export function initialActivityState(): ActivityStreamState {
  return { stages: [], activity: [], reasoning: [], terminal: null, seq: 0 };
}

/** A stage that has resolved does not go back to running. */
function isResolved(status: StageStatus): boolean {
  return status === "succeeded" || status === "failed";
}

/**
 * Resolve every stage still marked `running` at the moment the run ends.
 *
 * Without this the panel shows a spinner beside a finished run — the exact
 * self-contradiction that was reported ("complete" while something still turns).
 * A run that succeeded resolves its open stages as succeeded; a run that failed or
 * errored resolves them as failed, because we do not know that they finished.
 */
function settleStages(stages: ActivityStage[], outcome: "succeeded" | "failed"): ActivityStage[] {
  if (!stages.some((stage) => stage.status === "running")) return stages;
  return stages.map((stage) => (stage.status === "running" ? { ...stage, status: outcome } : stage));
}

/**
 * Fold one contract event into the panel state.
 *
 * The three properties this function exists to guarantee, each asserted in
 * `activityStream.test.ts`:
 *
 *   1. MONOTONIC. A stage may go upcoming → running → succeeded/failed and never back.
 *      A late duplicate `stage` event carrying `running` for a stage that already
 *      succeeded is dropped rather than applied.
 *   2. NEVER "COMPLETE" WHILE WORKING. Nothing here marks the run finished; only a
 *      `done` or `error` event does, and `describeActivityProgress` refuses to use
 *      completion wording before that.
 *   3. EXACTLY ONE TERMINAL STATE. The first `done`/`error` wins and every later event
 *      — including a second, contradicting terminal — is discarded.
 */
export function reduceActivityEvent(
  state: ActivityStreamState,
  event: ActivityStreamEvent
): ActivityStreamState {
  // Property 3: after a terminal state the stream says nothing more that can be believed.
  if (state.terminal) return state;

  const seq = state.seq + 1;

  if (event.type === "stage") {
    const key = String(event.data?.key || "");
    const label = String(event.data?.label || "");
    const status = event.data?.status;
    if (!key || (status !== "running" && status !== "succeeded" && status !== "failed")) return state;
    const index = state.stages.findIndex((stage) => stage.key === key);
    if (index === -1) {
      return { ...state, seq, stages: [...state.stages, { key, label, status }] };
    }
    const current = state.stages[index];
    // Property 1: a resolved stage is final. Only the label may still improve.
    const nextStatus = isResolved(current.status) ? current.status : status;
    if (nextStatus === current.status && (label || current.label) === current.label) return state;
    const stages = [...state.stages];
    stages[index] = { key, label: label || current.label, status: nextStatus };
    return { ...state, seq, stages };
  }

  if (event.type === "activity") {
    const text = String(event.data?.text || "").trim();
    if (!text) return state;
    return {
      ...state,
      seq,
      activity: [...state.activity, { id: `a${seq}`, text, detail: event.data?.detail ?? null }],
    };
  }

  if (event.type === "reasoning") {
    const text = String(event.data?.text || "");
    if (!text.trim()) return state;
    return { ...state, seq, reasoning: [...state.reasoning, { id: `r${seq}`, text }] };
  }

  if (event.type === "done") {
    const status = event.data?.status === "failed" ? "failed" : "succeeded";
    return {
      ...state,
      seq,
      stages: settleStages(state.stages, status),
      terminal: { status, messageId: event.data?.message_id },
    };
  }

  // event.type === "error": a transport- or run-level failure, and terminal.
  const message = String(event.data?.message || "").trim();
  return {
    ...state,
    seq,
    stages: settleStages(state.stages, "failed"),
    terminal: { status: "failed", message: message || "The request stopped before it finished." },
  };
}

// ── How the panel reads ──────────────────────────────────────────────────────

export type ActivityConnection = "idle" | "connecting" | "open" | "closed";

export interface ActivityProgressSummary {
  phase: "idle" | "connecting" | "working" | "succeeded" | "failed";
  headline: string;
  detail: string;
  /** True only while work is genuinely still in flight. Drives every spinner. */
  isWorking: boolean;
  completedCount: number;
  /** Number of stages seen SO FAR. Never presented as a total, because it is not one. */
  seenCount: number;
}

/**
 * Turn the state into the words on screen.
 *
 * The wording rules are the point of this function, and they exist because the shipped
 * screen contradicted itself:
 *
 *   - Completion wording ("Finished", "Complete") is used ONLY once a terminal event has
 *     arrived. Before that the headline is "Working".
 *   - The step count is never rendered as a fraction. We do not know how many stages a
 *     run will have, so "3 of 3" would be a claim we cannot support and would read as
 *     complete while work continues. It is rendered as a running count instead.
 *   - When every stage seen so far has resolved but the run has not ended, it says so
 *     plainly rather than falling silent or implying completion.
 */
export function describeActivityProgress(
  state: ActivityStreamState,
  connection: ActivityConnection = "idle"
): ActivityProgressSummary {
  const seenCount = state.stages.length;
  const completedCount = state.stages.filter((stage) => stage.status === "succeeded").length;
  const failedCount = state.stages.filter((stage) => stage.status === "failed").length;
  const running = state.stages.find((stage) => stage.status === "running");
  const lastLine = state.activity.at(-1)?.text || "";

  if (state.terminal) {
    if (state.terminal.status === "succeeded") {
      return {
        phase: "succeeded",
        headline: "Finished",
        detail: seenCount
          ? `${completedCount} step${completedCount === 1 ? "" : "s"} completed.`
          : "The request completed.",
        isWorking: false,
        completedCount,
        seenCount,
      };
    }
    return {
      phase: "failed",
      headline: "Stopped before finishing",
      detail: state.terminal.message || "The request did not complete. Nothing below is a finished answer.",
      isWorking: false,
      completedCount,
      seenCount,
    };
  }

  if (!seenCount && !state.activity.length) {
    // Nothing has been reported yet. Whether that is "starting" or "nothing running"
    // depends only on whether a stream is actually attached — never on a guess.
    if (connection === "connecting" || connection === "open") {
      return {
        phase: "connecting",
        headline: "Starting",
        detail: connection === "connecting"
          ? "Connecting to the run."
          : "Connected. Waiting for the first update.",
        isWorking: true,
        completedCount,
        seenCount,
      };
    }
    return {
      phase: "idle",
      headline: "Nothing running",
      detail: "Ask a question and the work will be narrated here as it happens.",
      isWorking: false,
      completedCount,
      seenCount,
    };
  }

  const parts: string[] = [];
  if (completedCount) parts.push(`${completedCount} step${completedCount === 1 ? "" : "s"} done`);
  if (failedCount) parts.push(`${failedCount} step${failedCount === 1 ? "" : "s"} failed`);
  if (running) parts.push(`now: ${running.label || running.key}`);
  else if (seenCount) parts.push("preparing the next step");
  else if (lastLine) parts.push(lastLine);

  return {
    phase: "working",
    headline: "Working",
    // Deliberately not a fraction and deliberately never the word "complete".
    detail: parts.length ? `${parts.join(" · ")} · still working` : "Still working.",
    isWorking: true,
    completedCount,
    seenCount,
  };
}

// ── Seeding from a thread that has already finished ──────────────────────────

export interface StoredProgressEvent {
  id?: string;
  label?: string;
  status?: string;
  at?: string;
}

export interface StoredProgressStep {
  id?: string;
  label?: string;
  status?: string;
}

/**
 * Build panel state for a thread whose run is over, from what the server stored.
 *
 * A finished thread has no live stream to attach to. Rendering the stored steps through
 * the SAME state shape is what stops the screen from having a second, differently-worded
 * account of the same run sitting next to the live one.
 */
export function activityStateFromStoredProgress(input: {
  steps?: StoredProgressStep[];
  events?: StoredProgressEvent[];
  status?: string;
}): ActivityStreamState {
  let state = initialActivityState();
  for (const step of input.steps || []) {
    const key = String(step?.id || step?.label || "");
    if (!key) continue;
    const status: StageStatus = step?.status === "running"
      ? "running"
      : step?.status === "failed" ? "failed" : step?.status === "done" ? "succeeded" : "running";
    // `upcoming` steps are not emitted: a step that has not started is not activity.
    if (step?.status === "upcoming") continue;
    state = reduceActivityEvent(state, { type: "stage", data: { key, label: String(step?.label || key), status } });
  }
  for (const event of input.events || []) {
    const text = String(event?.label || "").trim();
    if (!text) continue;
    state = reduceActivityEvent(state, { type: "activity", data: { text } });
  }
  if (input.status === "failed") {
    state = reduceActivityEvent(state, { type: "error", data: { message: "The request did not complete." } });
  } else if (input.status === "completed" || input.status === "ready") {
    state = reduceActivityEvent(state, { type: "done", data: { status: "succeeded" } });
  }
  return state;
}

// ── Wire format ──────────────────────────────────────────────────────────────

/**
 * Incremental `text/event-stream` parser.
 *
 * Frames arrive split across network chunks, so this holds a buffer and only emits a
 * frame once its blank-line terminator has been seen. Comment lines (`:` heartbeats)
 * and unknown event names are dropped silently, which is what the SSE spec requires and
 * what keeps an added backend event from breaking this client.
 */
export function createSseParser(): { push: (chunk: string) => ActivityStreamEvent[] } {
  let buffer = "";
  return {
    push(chunk: string): ActivityStreamEvent[] {
      buffer += chunk.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const out: ActivityStreamEvent[] = [];
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseSseFrame(frame);
        if (parsed) out.push(parsed);
        boundary = buffer.indexOf("\n\n");
      }
      return out;
    },
  };
}

function parseSseFrame(frame: string): ActivityStreamEvent | null {
  let name = "";
  const dataLines: string[] = [];
  for (const rawLine of frame.split("\n")) {
    if (!rawLine || rawLine.startsWith(":")) continue;
    const colon = rawLine.indexOf(":");
    const field = colon === -1 ? rawLine : rawLine.slice(0, colon);
    const value = colon === -1 ? "" : rawLine.slice(colon + 1).replace(/^ /, "");
    if (field === "event") name = value;
    else if (field === "data") dataLines.push(value);
  }
  if (!KNOWN_EVENTS.has(name) || !dataLines.length) return null;
  try {
    const data = JSON.parse(dataLines.join("\n"));
    if (!data || typeof data !== "object") return null;
    return { type: name, data } as ActivityStreamEvent;
  } catch {
    // A malformed frame is dropped rather than surfaced: it is not something the
    // reader can act on and it must not end an otherwise healthy run.
    return null;
  }
}

// ── Transport ────────────────────────────────────────────────────────────────

/**
 * How the client asks for `reasoning` events.
 *
 * NOT PART OF THE FROZEN CONTRACT. The contract states that `reasoning` events arrive
 * "only when the toggle is on" but does not state how the client asks for them. This
 * constant is the single place that decision lives, so aligning with whatever the
 * backend workstream publishes is a one-line change here and nowhere else.
 *
 * A boolean is not a credential, so a query parameter is appropriate for it. The TOKEN
 * is the thing that must never appear in a URL, and it does not — see `openActivityStream`.
 */
export const REASONING_QUERY_PARAM = "reasoning";

export const AUTH_TOKEN_STORAGE_KEY = "kontractai_token";
const USER_STORAGE_KEY = "kontractai_user";
const CLIENT_SCOPE_STORAGE_KEY = "kontractai_client_scope";

/** Path only — never carries a credential. */
export function activityStreamPath(threadId: string, showWorking: boolean): string {
  const base = `/api/docint/assistant/threads/${encodeURIComponent(threadId)}/stream`;
  return showWorking ? `${base}?${REASONING_QUERY_PARAM}=1` : base;
}

export interface ActivityStreamClose {
  ok: boolean;
  status?: number;
  message?: string;
}

export interface OpenActivityStreamOptions {
  threadId: string;
  showWorking: boolean;
  onEvent: (event: ActivityStreamEvent) => void;
  onOpen?: () => void;
  onClose?: (close: ActivityStreamClose) => void;
  signal?: AbortSignal;
  /** Injectable for tests. Production always uses the global `fetch`. */
  fetchImpl?: typeof fetch;
}

function readAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch { /* storage unavailable — the request will simply be rejected as unauthenticated */ }
  try {
    const scope = sessionStorage.getItem(CLIENT_SCOPE_STORAGE_KEY);
    if (scope && scope !== "all") headers["X-Client-Context"] = scope;
  } catch { /* sessionStorage unavailable — skip silently, matching apiClient */ }
  return headers;
}

/**
 * Open the stream and drive `onEvent` until it ends or is aborted.
 *
 * Never throws and never navigates. On a 401 the dead credential is cleared — the same
 * unconditional rule `apiClient.apiCallEx` documents — but the page is deliberately NOT
 * reloaded: a reload in the middle of a live run is indistinguishable, from the user's
 * side, from the run silently disappearing.
 */
export async function openActivityStream(options: OpenActivityStreamOptions): Promise<void> {
  const { threadId, showWorking, onEvent, onOpen, onClose, signal } = options;
  const doFetch = options.fetchImpl || fetch;
  const parser = createSseParser();
  try {
    const response = await doFetch(activityStreamPath(threadId, showWorking), {
      method: "GET",
      headers: readAuthHeaders(),
      signal,
      cache: "no-store",
    });
    if (response.status === 401) {
      try {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
      } catch { /* ignore */ }
      onClose?.({ ok: false, status: 401, message: "Your session has expired. Sign in again to follow this request." });
      return;
    }
    if (!response.ok) {
      onClose?.({ ok: false, status: response.status, message: "Live progress for this request is unavailable." });
      return;
    }
    onOpen?.();

    const body = (response as Response).body;
    if (!body || typeof body.getReader !== "function") {
      // No streaming body (some proxies, some test environments). Read it whole rather
      // than pretending nothing happened — a late narration is better than none.
      const text = await response.text();
      for (const event of parser.push(text)) onEvent(event);
      onClose?.({ ok: true });
      return;
    }

    const reader = body.getReader();
    // Built lazily: a reader that yields strings never needs one, and `TextDecoder` is
    // not a global in every environment this code is exercised in.
    let decoder: TextDecoder | null = null;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      let chunk: string;
      if (typeof value === "string") {
        chunk = value;
      } else {
        if (!decoder) decoder = new TextDecoder();
        chunk = decoder.decode(value, { stream: true });
      }
      for (const event of parser.push(chunk)) onEvent(event);
    }
    onClose?.({ ok: true });
  } catch (reason) {
    if (signal?.aborted) {
      onClose?.({ ok: true });
      return;
    }
    onClose?.({
      ok: false,
      message: reason instanceof Error && reason.message
        ? reason.message
        : "The connection to this request was lost.",
    });
  }
}
